import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Store,
  Package,
  ShoppingBag,
  MessageSquare,
  Shield,
  LogOut,
  ExternalLink,
  Pencil,
  Plus,
  Mail,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  Heart,
  FileText,
  Send,
  Trash2,
  History,
  TrendingUp,
  TrendingDown,
  Trophy,
  Clock,
  MapPin,
  Download,
  BarChart3,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, Cell, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";
import { getMyDashboard, type DashboardData } from "@/lib/profile.functions";

const fmtInt = new Intl.NumberFormat("sl-SI", { maximumFractionDigits: 0 }).format;
const fmtCurrency = new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR" }).format;
import { cancelMySubscription, resumeMySubscription, syncMySubscription } from "@/lib/subscription.functions";
import {
  getMyInquiries,
  getVendorInquiries,
  getAllInquiries,
} from "@/lib/inquiries.functions";
import { getMyOrders, getVendorOrders } from "@/lib/orders.functions";
import { getMyWishlist } from "@/lib/wishlist.functions";
import { getMyVendorVisitStats, type VisitRange } from "@/lib/vendor-visits.functions";

import {
  listMyReceivedQuotes,
  listMySentQuotes,
  respondToQuote,
  sendQuote,
  deleteQuote,
  markQuotePaid,
  type Quote,
  type QuoteStatus,
} from "@/lib/quotes.functions";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({ meta: [{ title: "Moj račun — kupish.si" }] }),
  component: Page,
});

type TabId =
  | "vendor-pregled"
  | "buyer-pregled"
  | "narocila"
  | "zelje"
  | "povprasevanja-moja"
  | "predracuni-prejeti"
  | "trgovina"
  | "izdelki"
  | "prodaja"
  | "povprasevanja-strank"
  | "predracuni-poslani"
  | "admin";

function Page() {
  const { signOut, user } = useAuth();
  const fetchDashboard = useServerFn(getMyDashboard);
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", user?.id],
    queryFn: () => fetchDashboard(),
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const fetchMyInquiries = useServerFn(getMyInquiries);
  const fetchVendorInquiries = useServerFn(getVendorInquiries);
  const fetchAllInquiries = useServerFn(getAllInquiries);
  const fetchReceivedQuotes = useServerFn(listMyReceivedQuotes);
  const fetchSentQuotes = useServerFn(listMySentQuotes);

  const myInq = useQuery({
    queryKey: ["my-inquiries", user?.id],
    queryFn: () => fetchMyInquiries(),
    enabled: !!user,
  });
  const isVendorRole = !!data?.vendor;
  const isAdminRole = !!data?.roles.includes("admin");
  const vendorInq = useQuery({
    queryKey: ["vendor-inquiries", user?.id],
    queryFn: () => fetchVendorInquiries(),
    enabled: !!user && isVendorRole,
  });
  const adminInq = useQuery({
    queryKey: ["all-inquiries", user?.id],
    queryFn: () => fetchAllInquiries(),
    enabled: !!user && isAdminRole,
  });
  const receivedQuotes = useQuery({
    queryKey: ["received-quotes", user?.id],
    queryFn: () => fetchReceivedQuotes(),
    enabled: !!user,
  });
  const sentQuotes = useQuery({
    queryKey: ["sent-quotes", user?.id],
    queryFn: () => fetchSentQuotes(),
    enabled: !!user && isVendorRole,
  });
  const fetchMyOrders = useServerFn(getMyOrders);
  const fetchMyWishlist = useServerFn(getMyWishlist);
  const fetchVendorOrders = useServerFn(getVendorOrders);
  const myOrders = useQuery({
    queryKey: ["my-orders", user?.id],
    queryFn: () => fetchMyOrders(),
    enabled: !!user,
  });
  const myWishlist = useQuery({
    queryKey: ["my-wishlist", user?.id],
    queryFn: () => fetchMyWishlist(),
    enabled: !!user,
  });
  const vendorOrders = useQuery({
    queryKey: ["vendor-orders", user?.id],
    queryFn: () => fetchVendorOrders(),
    enabled: !!user && isVendorRole,
  });


  const [tab, setTab] = useState<TabId>("buyer-pregled");

  const queryClient = useQueryClient();
  const runSyncMySubscription = useServerFn(syncMySubscription);

  useEffect(() => {
    if (data) {
      const isV = data.roles.includes("vendor") || !!data.vendor;
      setTab(isV ? "vendor-pregled" : "buyer-pregled");
    }
  }, [data]);

  // Auto-sync Stripe on every dashboard mount so the shop lock/unlock
  // reflects reality: picks up missed webhook activations AND lapses
  // (canceled/past_due) that should re-lock the shop.
  useEffect(() => {
    if (!data?.vendor) return;
    let cancelled = false;
    (async () => {
      try {
        const prevActive = data.subscription?.status === "active" || data.subscription?.status === "trialing";
        const res = await runSyncMySubscription();
        if (cancelled || !res.ok) return;
        if (res.synced > 0) {
          await queryClient.invalidateQueries({ queryKey: ["dashboard", user?.id] });
          if (res.activated && !prevActive) {
            toast.success("Naročnina potrjena — trgovina je aktivirana.");
          }
        }
      } catch {
        // Silent: background self-heal.
      }
    })();
    return () => { cancelled = true; };
  }, [data, runSyncMySubscription, queryClient, user?.id]);



  if (isLoading)
    return (
      <div className="mx-auto max-w-7xl px-6 py-24 text-muted-foreground">
        Nalagam…
      </div>
    );
  if (error)
    return (
      <div className="mx-auto max-w-7xl px-6 py-24 text-destructive">
        Napaka: {(error as Error).message}
      </div>
    );
  if (!data) return null;

  const isVendor = data.roles.includes("vendor") || !!data.vendor;
  const isAdmin = data.roles.includes("admin");
  const sub = data.subscription;
  const vendor = data.vendor;
  const profile = data.profile;
  const isActiveMember = sub?.status === "active" || sub?.status === "trialing";

  const displayName =
    profile?.display_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
    data.email ||
    "Uporabnik";


  const initials = displayName
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const unreadMy = (myInq.data ?? []).filter((r) => !r.read_at).length;
  const unreadVendor = (vendorInq.data ?? []).filter((r) => !r.read_at).length;

  const buyerNavBase: { id: TabId; label: string; icon: any; badge?: number }[] = [
    { id: "narocila", label: "Moja naročila", icon: ShoppingBag },
    { id: "zelje", label: "Seznam želja", icon: Heart },
    {
      id: "povprasevanja-moja",
      label: "Moja povpraševanja",
      icon: MessageSquare,
      badge: unreadMy || undefined,
    },
    { id: "predracuni-prejeti", label: "Moji predračuni", icon: FileText },
  ];
  const buyerNav = [
    { id: "buyer-pregled" as TabId, label: "Pregled", icon: LayoutDashboard },
    ...buyerNavBase,
  ];

  const vendorNav: { id: TabId; label: string; icon: any; badge?: number; to?: "/moji-izdelki" | "/prodaja" | "/trgovina" }[] = [
    { id: "vendor-pregled", label: "Pregled", icon: LayoutDashboard },
    { id: "trgovina", label: "Moja trgovina", icon: Store, to: "/trgovina" },
    { id: "izdelki", label: "Moji izdelki", icon: Package, to: "/moji-izdelki" },
    { id: "prodaja", label: "Zgodovina prodaj", icon: History, to: "/prodaja" },
    {
      id: "povprasevanja-strank",
      label: "Povpraševanja strank",
      icon: MessageSquare,
      badge: unreadVendor || undefined,
    },
    { id: "predracuni-poslani", label: "Poslani predračuni", icon: Send },
  ];

  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <div className="size-12 sm:size-14 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center font-serif text-lg sm:text-xl">
            {initials || "U"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="eyebrow">Moj račun</div>
            <h1 className="font-serif text-xl sm:text-3xl md:text-4xl mt-1 leading-tight truncate">
              Pozdravljeni
            </h1>
            <p className="mt-1 text-xs sm:text-sm text-muted-foreground flex items-center gap-2 min-w-0">
              <Mail className="size-3.5 shrink-0" /> <span className="truncate">{data.email}</span>
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:shrink-0">
          <button
            onClick={() => signOut()}
            className="inline-flex items-center justify-center gap-2 border border-border px-3 py-2 text-sm hover:bg-muted transition-colors"
          >
            <LogOut className="size-4" /> Odjava
          </button>
        </div>
      </div>


      {isVendor && vendor && !vendor.payout_ready && (
        <div className="mb-8 border border-destructive bg-destructive/10 p-5 text-sm flex flex-wrap items-start gap-4 justify-between">
          <div className="flex-1 min-w-[260px]">
            <p className="font-medium text-destructive">Dopolnite podatke trgovine</p>
            <p className="mt-2 text-muted-foreground">
              Za sprejemanje naročil in izdajo predračunov morate vnesti <strong>naslov in TRR (SI56…)</strong>. Brez teh podatkov vam stranke ne morejo poslati povpraševanj.
            </p>
          </div>
          <Link to="/trgovina" className="bg-destructive text-destructive-foreground px-4 py-2.5 text-sm hover:opacity-90 self-center">
            Dopolni podatke →
          </Link>
        </div>
      )}


      <div className="grid md:grid-cols-[240px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="md:sticky md:top-24 self-start">
          {isVendor && (
            <NavSection
              label="KOT PRODAJALEC"
              items={vendorNav}
              activeTab={tab}
              onSelect={setTab}
            />
          )}
          {isVendor && (
            <div className="flex items-center justify-center my-4">
              <div className="flex-1 h-px bg-border" />
              <Heart className="size-4 text-primary mx-2" />
              <div className="flex-1 h-px bg-border" />
            </div>
          )}
          <NavSection
            label="KOT KUPEC"
            items={buyerNav}
            activeTab={tab}
            onSelect={setTab}
            className={isVendor ? "" : ""}
          />
          {isAdmin && (
            <NavSection
              label="Skrbnik"
              items={[{ id: "admin", label: "Skrbniške nastavitve", icon: Shield }]}
              activeTab={tab}
              onSelect={setTab}
              className="mt-6"
            />
          )}
        </aside>

        {/* Content */}
        <div className="min-w-0">
          {tab === "vendor-pregled" && (
            <VendorPregled
              data={data}
              isActiveMember={isActiveMember}
              unreadVendor={unreadVendor}
              sentQuotesCount={(sentQuotes.data ?? []).length}
              sentQuotesAll={sentQuotes.data ?? []}
              vendorInqAll={vendorInq.data ?? []}
              vendorItems={vendorOrders.data?.items ?? []}
              myOrdersCount={(myOrders.data?.orders ?? []).length}
              receivedQuotesCount={(receivedQuotes.data ?? []).length}
              onTab={setTab}
            />
          )}
          {tab === "buyer-pregled" && (
            <BuyerPregled
              data={data}
              unreadMy={unreadMy}
              receivedQuotesCount={(receivedQuotes.data ?? []).length}
              myOrders={myOrders.data?.orders ?? []}
              wishlistCount={(myWishlist.data?.products ?? []).length}
              onTab={setTab}
            />
          )}


          {tab === "narocila" && <NarocilaList />}
          {tab === "zelje" && <ZeljeList />}
          {tab === "povprasevanja-moja" && (
            <InquiriesBlock
              title="Moja povpraševanja"
              emptyText="Še niste poslali povpraševanj."
              rows={myInq.data ?? []}
            />
          )}
          {tab === "predracuni-prejeti" && (
            <ReceivedQuotes rows={receivedQuotes.data ?? []} userId={user?.id} />
          )}

          {tab === "trgovina" && isVendor && (
            <Navigate to="/trgovina" />
          )}

          {tab === "izdelki" && (isVendor || isAdmin) && (
            <Navigate to="/moji-izdelki" />
          )}

          {tab === "prodaja" && (isVendor || isAdmin) && <Navigate to="/prodaja" />}

          {tab === "povprasevanja-strank" && isVendor && (
            <InquiriesBlock
              title="Povpraševanja strank"
              emptyText="Še ni prejetih povpraševanj."
              rows={vendorInq.data ?? []}
              showCustomer
            />
          )}

          {tab === "predracuni-poslani" && isVendor && (
            <SentQuotes rows={sentQuotes.data ?? []} userId={user?.id} />
          )}

          {tab === "admin" && isAdmin && (
            <>
              <AdminTab />
              {isAdminRole && (
                <div className="mt-6">
                  <InquiriesBlock
                    title="Vsa povpraševanja"
                    emptyText="Še ni nobenih povpraševanj."
                    rows={adminInq.data ?? []}
                    showCustomer
                    showVendor
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------- Sidebar ---------- */

function NavSection({
  label,
  items,
  activeTab,
  onSelect,
  className,
}: {
  label: string;
  items: { id: TabId; label: string; icon: any; badge?: number; to?: "/moji-izdelki" | "/prodaja" | "/trgovina" }[];
  activeTab: TabId;
  onSelect: (t: TabId) => void;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="eyebrow mb-2 px-1">{label}</div>
      <nav className="flex flex-col gap-1">
        {items.map((n) => {
          const Icon = n.icon;
          const active = activeTab === n.id;
          const itemClassName = `group flex items-center justify-between gap-3 px-3 py-2.5 text-sm whitespace-nowrap transition-colors ${
            active
              ? "bg-primary text-primary-foreground"
              : "hover:bg-muted text-foreground"
          }`;

          if (n.to) {
            return (
              <Link
                key={n.id}
                to={n.to}
                className={itemClassName}
                inactiveProps={{ className: itemClassName }}
                activeProps={{ className: "group flex items-center justify-between gap-3 px-3 py-2.5 text-sm whitespace-nowrap transition-colors bg-primary text-primary-foreground" }}
              >
                <span className="flex items-center gap-2.5">
                  <Icon className="size-4" />
                  {n.label}
                </span>
                {n.badge ? (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      active
                        ? "bg-primary-foreground text-primary"
                        : "bg-accent text-accent-foreground"
                    }`}
                  >
                    {n.badge}
                  </span>
                ) : null}
              </Link>
            );
          }

          return (
            <button
              key={n.id}
              onClick={() => onSelect(n.id)}
              className={itemClassName}
            >
              <span className="flex items-center gap-2.5">
                <Icon className="size-4" />
                {n.label}
              </span>
              {n.badge ? (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    active
                      ? "bg-primary-foreground text-primary"
                      : "bg-accent text-accent-foreground"
                  }`}
                >
                  {n.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

/* ---------- Tab: Pregled ---------- */

function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  tone = 1,
}: {
  label: string;
  value: string | number;
  icon: any;
  hint?: string;
  tone?: 1 | 2 | 3 | 4 | 5;
}) {
  return (
    <div
      className="relative border border-border p-5 h-full hover:border-accent transition-colors overflow-hidden"
      style={{ background: `var(--tone-${tone}-soft)` }}
    >
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: `var(--tone-${tone})` }}
      />
      <div className="flex items-start justify-between gap-3">
        <div className="eyebrow">{label}</div>
        <Icon className="size-4" style={{ color: `var(--tone-${tone})` }} />
      </div>
      <div className="font-serif text-3xl mt-3">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function SubscriptionDetails({
  isActiveMember,
  subscription,
  email,
}: {
  isActiveMember: boolean;
  subscription: { status: string; product_name: string | null; amount: number | null; currency: string; current_period_end: string | null; cancel_at_period_end: boolean } | null;
  email: string | null;
}) {
  const qc = useQueryClient();
  const cancelFn = useServerFn(cancelMySubscription);
  const resumeFn = useServerFn(resumeMySubscription);
  const syncFn = useServerFn(syncMySubscription);

  const cancelMut = useMutation({
    mutationFn: () => cancelFn(),
    onSuccess: async () => {
      toast.success("Naročnina bo prekinjena ob koncu obračunskega obdobja. Trgovina ostane aktivna do takrat.");
      await syncFn().catch(() => {});
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resumeMut = useMutation({
    mutationFn: () => resumeFn(),
    onSuccess: async () => {
      toast.success("Naročnina obnovljena. Naslednje plačilo se bo izvedlo kot običajno.");
      await syncFn().catch(() => {});
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const syncMut = useMutation({
    mutationFn: () => syncFn(),
    onSuccess: (res) => {
      if (res.ok && res.synced > 0) {
        toast.success(res.activated ? "Naročnina potrjena — trgovina je aktivirana." : "Status posodobljen.");
      } else if (res.ok) {
        toast.info("Status je že aktualen.");
      }
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });


  const periodEnd = subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString("sl-SI") : null;
  const canceled = Boolean(subscription?.cancel_at_period_end);

  return (
    <div className="border border-border p-6" style={{ background: "var(--tone-3-soft)" }}>
      <div className="eyebrow">Naročnina — letna najemnina trgovine</div>
      {subscription ? (
        <>
          <h3 className="font-serif text-2xl mt-2">{subscription.product_name ?? "Najem trgovine"}</h3>
          <div className="mt-3 grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Status</div>
              <div className={`mt-1 font-medium ${canceled ? "text-destructive" : isActiveMember ? "text-accent" : "text-destructive"}`}>
                {canceled ? "Prekinitev ob koncu obdobja" : subscription.status}
              </div>
            </div>
            {subscription.amount != null && (
              <div>
                <div className="text-xs text-muted-foreground">Cena</div>
                <div className="mt-1 font-medium">{subscription.amount} {subscription.currency.toUpperCase()} / leto</div>
              </div>
            )}
            {periodEnd && (
              <div>
                <div className="text-xs text-muted-foreground">{canceled ? "Trgovina aktivna do" : "Naslednje plačilo"}</div>
                <div className="mt-1 font-medium">{periodEnd}</div>
              </div>
            )}
          </div>

          {canceled ? (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <p className="text-sm text-muted-foreground flex-1 min-w-[240px]">
                Naročnina je preklicana. Naslednje plačilo se ne bo izvedlo. Trgovina ostane aktivna do {periodEnd}.
              </p>
              <button
                onClick={() => resumeMut.mutate()}
                disabled={resumeMut.isPending}
                className="bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-50"
              >
                {resumeMut.isPending ? "Obnavljam…" : "Obnovi naročnino"}
              </button>
            </div>
          ) : isActiveMember ? (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <p className="text-sm text-muted-foreground flex-1 min-w-[240px]">
                Ob preklicu se naslednji odtegljaj ne izvede. Trgovina ostane aktivna do konca trenutnega obdobja.
              </p>
              <button
                onClick={() => {
                  if (confirm("Ste prepričani, da želite preklicati naročnino? Naslednje plačilo se ne bo izvedlo.")) {
                    cancelMut.mutate();
                  }
                }}
                disabled={cancelMut.isPending}
                className="border border-destructive text-destructive px-5 py-2.5 text-sm hover:bg-destructive/10 disabled:opacity-50"
              >
                {cancelMut.isPending ? "Preklicam…" : "Prekliči naročnino"}
              </button>
            </div>
          ) : (
            <a
              href={`https://buy.stripe.com/14A4gy12K2WM0ZS8uD6c00q${email ? `?prefilled_email=${encodeURIComponent(email)}` : ""}`}
              target="_blank" rel="noopener noreferrer"
              className="mt-5 inline-flex bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90"
            >
              Aktiviraj naročnino →
            </a>
          )}
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-muted-foreground">Za aktivacijo trgovine je potrebna aktivna letna naročnina.</p>
          <a
            href={`https://buy.stripe.com/14A4gy12K2WM0ZS8uD6c00q${email ? `?prefilled_email=${encodeURIComponent(email)}` : ""}`}
            target="_blank" rel="noopener noreferrer"
            className="mt-4 inline-flex bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90"
          >
            Aktiviraj naročnino →
          </a>
        </>
      )}

      <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center gap-3 text-sm">
        <Link to="/najemnina" className="text-accent hover:underline">
          Podrobnosti najemnine →
        </Link>
        <button
          type="button"
          onClick={() => syncMut.mutate()}
          disabled={syncMut.isPending}
          className="ml-auto text-muted-foreground hover:text-foreground underline disabled:opacity-50"
        >
          {syncMut.isPending ? "Preverjam…" : "Preveri status pri Stripe"}
        </button>
      </div>
    </div>

  );
}


type RangeKey = "1m" | "1y" | "2y" | "5y" | "all";
const RANGE_OPTIONS: { key: RangeKey; label: string }[] = [
  { key: "1m", label: "1 mesec" },
  { key: "1y", label: "1 leto" },
  { key: "2y", label: "2 leti" },
  { key: "5y", label: "5 let" },
  { key: "all", label: "Od začetka" },
];

function SalesChart({
  items,
}: {
  items: Array<{ order_status: string; line_total: number; created_at: string }>;
}) {
  const [range, setRange] = useState<RangeKey>("1y");
  const paidStatuses = ["paid", "shipped", "delivered"];

  const now = new Date();
  const cutoff = (() => {
    const d = new Date(now);
    if (range === "1m") d.setMonth(d.getMonth() - 1);
    else if (range === "1y") d.setFullYear(d.getFullYear() - 1);
    else if (range === "2y") d.setFullYear(d.getFullYear() - 2);
    else if (range === "5y") d.setFullYear(d.getFullYear() - 5);
    else return null;
    return d;
  })();

  const paid = items.filter(
    (i) =>
      paidStatuses.includes(i.order_status) &&
      (!cutoff || new Date(i.created_at) >= cutoff),
  );

  const useDaily = range === "1m";
  const bucket = (d: Date) =>
    useDaily
      ? d.toISOString().slice(0, 10)
      : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  const map = new Map<string, number>();
  for (const i of paid) {
    const k = bucket(new Date(i.created_at));
    map.set(k, (map.get(k) ?? 0) + Number(i.line_total));
  }

  // Build a continuous series so empty periods show as 0
  const series: { key: string; label: string; value: number }[] = [];
  if (paid.length > 0 || cutoff) {
    const start = cutoff ?? new Date(Math.min(...paid.map((i) => +new Date(i.created_at)), +now));
    const cursor = new Date(start);
    if (useDaily) cursor.setHours(0, 0, 0, 0);
    else { cursor.setDate(1); cursor.setHours(0, 0, 0, 0); }
    while (cursor <= now) {
      const k = bucket(cursor);
      series.push({
        key: k,
        label: useDaily
          ? cursor.toLocaleDateString("sl-SI", { day: "2-digit", month: "2-digit" })
          : cursor.toLocaleDateString("sl-SI", { month: "short", year: "2-digit" }),
        value: map.get(k) ?? 0,
      });
      if (useDaily) cursor.setDate(cursor.getDate() + 1);
      else cursor.setMonth(cursor.getMonth() + 1);
      if (series.length > 400) break;
    }
  }

  const total = series.reduce((s, p) => s + p.value, 0);

  return (
    <div
      className="border border-border p-5"
      style={{ background: "var(--tone-2-soft)" }}
    >
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="eyebrow">Prodaja skozi čas</div>
          <div className="font-serif text-2xl mt-1">
            {fmtCurrency(total)}{" "}
            <span className="text-sm text-muted-foreground font-sans">
              v izbranem obdobju
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {RANGE_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setRange(o.key)}
              className={`px-3 py-1.5 text-xs border transition-colors ${
                range === o.key
                  ? "border-transparent text-primary-foreground"
                  : "border-border hover:bg-muted"
              }`}
              style={
                range === o.key
                  ? { background: "var(--tone-2)" }
                  : undefined
              }
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="h-64 -ml-2">
        {series.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
            Še ni prodaje v tem obdobju.
          </div>
        ) : (
          <SalesChartCanvas data={series} />
        )}
      </div>
    </div>
  );
}

function SalesChartCanvas({
  data,
}: {
  data: { key: string; label: string; value: number }[];
}) {
  // Imported statically at top of file
  const step = Math.max(1, Math.ceil(data.length / 8));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--tone-2)" stopOpacity={0.45} />
            <stop offset="100%" stopColor="var(--tone-2)" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
          axisLine={{ stroke: "var(--border)" }}
          tickLine={false}
          interval={step - 1}
        />
        <YAxis
          tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => fmtCurrency(Number(v))}
          width={56}
        />
        <Tooltip
          contentStyle={{
            background: "var(--background)",
            border: "1px solid var(--border)",
            borderRadius: 4,
            fontSize: 12,
          }}
          labelStyle={{ color: "var(--foreground)" }}
          formatter={(v: number) => [fmtCurrency(Number(v)), "Prihodek"]}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke="var(--tone-2)"
          strokeWidth={2}
          fill="url(#salesFill)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function VendorPregled({
  data,
  isActiveMember,
  unreadVendor,
  sentQuotesCount,
  sentQuotesAll,
  vendorInqAll,
  vendorItems,
  myOrdersCount,
  receivedQuotesCount,
  onTab,
}: {
  data: any;
  isActiveMember: boolean;
  unreadVendor: number;
  sentQuotesCount: number;
  sentQuotesAll: Array<{ id: string; status: string; sent_at: string | null; responded_at: string | null; created_at: string; total: number }>;
  vendorInqAll: Array<{ id: string; created_at: string }>;
  vendorItems: Array<{ order_id: string; order_status: string; line_total: number; quantity: number; created_at: string; title?: string; shipping_address?: { city?: string; country?: string } | null }>;
  myOrdersCount: number;
  receivedQuotesCount: number;
  onTab: (t: TabId) => void;
}) {
  const vendor = data.vendor;
  const paidStatuses = ["paid", "shipped", "delivered"];
  const orderIds = new Set(vendorItems.map((i) => i.order_id));
  const quoteRequestedOrders = new Set(vendorItems.filter((i) => i.order_status === "quote_requested").map((i) => i.order_id));
  const paidOrders = new Set(vendorItems.filter((i) => i.order_status === "paid").map((i) => i.order_id));
  const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
  const revenueAll = vendorItems.filter((i) => paidStatuses.includes(i.order_status)).reduce((s, i) => s + i.line_total, 0);
  const revenueMonth = vendorItems.filter((i) => paidStatuses.includes(i.order_status) && new Date(i.created_at) >= monthStart).reduce((s, i) => s + i.line_total, 0);
  const unitsSold = vendorItems.filter((i) => paidStatuses.includes(i.order_status)).reduce((s, i) => s + i.quantity, 0);
  const [subOpen, setSubOpen] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow">Pregled prodajalca</div>
        <h2 className="font-serif text-2xl mt-1">
          {vendor?.store_name ?? "Moja trgovina"}
        </h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/moji-izdelki" className="text-left">
          <StatCard label="Izdelki" value={fmtInt(data.productCount)} icon={Package} hint="V moji trgovini" tone={2} />
        </Link>
        <Link to="/prodaja" className="text-left">
          <StatCard label="Naročila" value={fmtInt(orderIds.size)} icon={ShoppingBag} hint="Skupaj prejeta" tone={3} />
        </Link>
        <Link to="/prodaja" className="text-left">
          <StatCard label="Prihodek (vse)" value={fmtCurrency(revenueAll)} icon={History} hint={`${fmtInt(unitsSold)} prodanih kosov`} tone={1} />
        </Link>
        <Link to="/prodaja" className="text-left">
          <StatCard label="Prihodek (mesec)" value={fmtCurrency(revenueMonth)} icon={History} tone={4} />
        </Link>
      </div>

      <OnboardingChecklist vendor={vendor} productCount={data.productCount} />

      <Reminders sentQuotes={sentQuotesAll} vendorItems={vendorItems} onTab={onTab} />

      <PeriodComparison vendorItems={vendorItems} />

      <SalesChart items={vendorItems} />

      <VisitsChart />


      <div className="grid lg:grid-cols-2 gap-6">
        <TopProducts vendorItems={vendorItems} />
        <StatusBreakdown vendorItems={vendorItems} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <ConversionStats vendorItems={vendorItems} inquiries={vendorInqAll} sentQuotes={sentQuotesAll} />
        <GeoBreakdown vendorItems={vendorItems} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <button type="button" onClick={() => onTab("povprasevanja-strank")} className="text-left">
          <StatCard label="Povpraševanja" value={vendorInqAll.length} icon={MessageSquare} hint={unreadVendor ? `${unreadVendor} neprebranih` : "E-poštna vprašanja strank"} tone={5} />
        </button>
        <button type="button" onClick={() => onTab("predracuni-poslani")} className="text-left">
          <StatCard label="Predračuni" value={sentQuotesCount} icon={Send} hint="Poslani" tone={2} />
        </button>
        <Link to="/prodaja" className="text-left">
          <StatCard label="Za odpošiljanje" value={paidOrders.size} icon={Package} hint="Plačana naročila" tone={4} />
        </Link>
        <button type="button" onClick={() => setSubOpen((v) => !v)} className="text-left">
          <StatCard
            label="Naročnina"
            value={isActiveMember ? (data.subscription?.cancel_at_period_end ? "Preklic" : "Aktivna") : "Neaktivna"}
            icon={CreditCard}
            tone={3}
            hint={data.subscription?.current_period_end ? `${data.subscription?.cancel_at_period_end ? "Konec" : "Do"} ${new Date(data.subscription.current_period_end).toLocaleDateString("sl-SI")}` : "Klikni za upravljanje"}
          />
        </button>
      </div>

      {subOpen && (
        <SubscriptionDetails
          isActiveMember={isActiveMember}
          subscription={data.subscription}
          email={data.email}
        />
      )}

      {!isActiveMember && (
        <div className="border border-destructive/40 bg-destructive/5 p-6 flex items-start gap-4 flex-wrap">
          <AlertCircle className="size-5 text-destructive shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="eyebrow text-destructive">Naročnina ni aktivna</div>
            <p className="mt-1 text-sm">Za prikaz izdelkov in upravljanje trgovine je potrebna aktivna letna naročnina.</p>
          </div>
          <a href={`https://buy.stripe.com/14A4gy12K2WM0ZS8uD6c00q${data.email ? `?prefilled_email=${encodeURIComponent(data.email)}` : ""}`}
            target="_blank" rel="noopener noreferrer"
            className="bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90">
            Aktiviraj →
          </a>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <QuickAction icon={Store} title="Moja trgovina" desc="Uredi javni profil." onClick={() => onTab("trgovina")} />
        <QuickAction icon={Package} title="Moji izdelki" desc="Dodaj ali uredi izdelke." to="/moji-izdelki" />
        <ExportSalesCard vendorItems={vendorItems} storeName={vendor?.store_name ?? "trgovina"} />
        {vendor?.slug && (
          <Link to="/prodajalci/$slug" params={{ slug: vendor.slug }} target="_blank" rel="noreferrer"
            className="border border-border p-5 hover:border-accent transition-colors h-full block">
            <ExternalLink className="size-5 text-accent" />
            <div className="font-serif text-lg mt-3">Predogled trgovine</div>
            <p className="mt-1 text-sm text-muted-foreground">Kot ga vidijo kupci.</p>
          </Link>
        )}
      </div>
    </div>
  );
}

/* ---------- Vendor dashboard widgets ---------- */

type VItem = {
  order_id: string;
  order_status: string;
  line_total: number;
  quantity: number;
  created_at: string;
  title?: string;
  shipping_address?: { city?: string; country?: string } | null;
};

const PAID_STATUSES = ["paid", "shipped", "delivered"];

function OnboardingChecklist({
  vendor,
  productCount,
}: {
  vendor: DashboardData["vendor"];
  productCount: number;
}) {
  if (!vendor) return null;
  const items: Array<{ done: boolean; label: string; to: string }> = [
    { done: Boolean(vendor.image_url), label: "Naložite profilno sliko trgovine", to: "/profil" },
    { done: Boolean(vendor.banner_url), label: "Naložite naslovno (banner) sliko", to: "/profil" },
    { done: Boolean(vendor.description && vendor.description.length >= 60), label: "Napišite opis trgovine (vsaj 60 znakov)", to: "/profil" },
    { done: productCount >= 3, label: "Dodajte vsaj 3 izdelke", to: "/moji-izdelki" },
    { done: Boolean(vendor.payout_ready), label: "Izpolnite naslov in IBAN za izplačila", to: "/profil" },
  ];
  const completed = items.filter((i) => i.done).length;
  if (completed === items.length) return null;
  const pct = Math.round((completed / items.length) * 100);
  return (
    <div className="border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Pripravljenost trgovine</div>
          <h3 className="font-serif text-lg mt-1">Dokončajte profil trgovine</h3>
        </div>
        <div className="text-sm text-muted-foreground">{completed}/{items.length} dokončano ({pct}%)</div>
      </div>
      <div className="mt-3 h-2 bg-muted rounded">
        <div className="h-2 bg-accent rounded transition-all" style={{ width: `${pct}%` }} />
      </div>
      <ul className="mt-4 space-y-2">
        {items.map((it) => (
          <li key={it.label} className="flex items-center gap-3 text-sm">
            <CheckCircle2 className={`size-4 shrink-0 ${it.done ? "text-accent" : "text-muted-foreground/40"}`} />
            {it.done ? (
              <span className="line-through text-muted-foreground">{it.label}</span>
            ) : (
              <Link to={it.to} className="hover:text-accent underline-offset-4 hover:underline">{it.label}</Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}



function Reminders({
  sentQuotes,
  vendorItems,
  onTab,
}: {
  sentQuotes: Array<{ status: string; sent_at: string | null; created_at: string; responded_at: string | null }>;
  vendorItems: VItem[];
  onTab: (t: TabId) => void;
}) {
  const now = Date.now();
  const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;

  const stalePending = sentQuotes.filter((q) => {
    if (q.status !== "sent" || q.responded_at) return false;
    const sentAt = q.sent_at ? new Date(q.sent_at).getTime() : new Date(q.created_at).getTime();
    return now - sentAt > THREE_DAYS;
  }).length;

  const paidNotShipped = new Set(
    vendorItems.filter((i) => i.order_status === "paid").map((i) => i.order_id),
  ).size;

  const quoteRequests = new Set(
    vendorItems.filter((i) => i.order_status === "quote_requested").map((i) => i.order_id),
  ).size;

  if (stalePending === 0 && paidNotShipped === 0 && quoteRequests === 0) return null;

  return (
    <div className="border border-border p-5" style={{ background: "var(--tone-4-soft)" }}>
      <div className="flex items-center gap-2 mb-3">
        <Clock className="size-4" style={{ color: "var(--tone-4)" }} />
        <div className="eyebrow">Opomniki</div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        {quoteRequests > 0 && (
          <button onClick={() => onTab("predracuni-poslani")} className="text-left border border-border bg-background p-3 hover:border-accent transition-colors">
            <div className="font-serif text-2xl" style={{ color: "var(--tone-5)" }}>{quoteRequests}</div>
            <div className="text-muted-foreground">zahtev za predračun čaka</div>
          </button>
        )}
        {stalePending > 0 && (
          <button onClick={() => onTab("predracuni-poslani")} className="text-left border border-border bg-background p-3 hover:border-accent transition-colors">
            <div className="font-serif text-2xl" style={{ color: "var(--tone-1)" }}>{stalePending}</div>
            <div className="text-muted-foreground">predračunov brez odgovora &gt; 3 dni</div>
          </button>
        )}
        {paidNotShipped > 0 && (
          <button onClick={() => onTab("prodaja")} className="text-left border border-border bg-background p-3 hover:border-accent transition-colors">
            <div className="font-serif text-2xl" style={{ color: "var(--tone-4)" }}>{paidNotShipped}</div>
            <div className="text-muted-foreground">plačanih naročil za odpošiljanje</div>
          </button>
        )}
      </div>
    </div>
  );
}

function PeriodComparison({ vendorItems }: { vendorItems: VItem[] }) {
  const paid = vendorItems.filter((i) => PAID_STATUSES.includes(i.order_status));
  const now = new Date();

  const sumBetween = (start: Date, end: Date) =>
    paid
      .filter((i) => {
        const t = +new Date(i.created_at);
        return t >= +start && t < +end;
      })
      .reduce((s, i) => s + i.line_total, 0);

  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const prevYearStart = new Date(now.getFullYear() - 1, 0, 1);
  const prevYearSameDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

  const thisMonth = sumBetween(monthStart, now);
  const prevMonth = sumBetween(prevMonthStart, monthStart);
  const thisYear = sumBetween(yearStart, now);
  const prevYearYTD = sumBetween(prevYearStart, prevYearSameDate);

  const calc = (cur: number, prev: number): { pct: number; dir: "up" | "down" | "flat" } => {
    if (prev === 0) return { pct: cur > 0 ? 100 : 0, dir: cur > 0 ? "up" : "flat" };
    const pct = ((cur - prev) / prev) * 100;
    return { pct, dir: pct > 0.5 ? "up" : pct < -0.5 ? "down" : "flat" };
  };

  const m = calc(thisMonth, prevMonth);
  const y = calc(thisYear, prevYearYTD);

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <ComparisonCard
        label="Mesec vs. prejšnji"
        current={thisMonth}
        previous={prevMonth}
        pct={m.pct}
        dir={m.dir}
        tone={3}
      />
      <ComparisonCard
        label="Leto vs. prejšnje (do danes)"
        current={thisYear}
        previous={prevYearYTD}
        pct={y.pct}
        dir={y.dir}
        tone={1}
      />
    </div>
  );
}

function ComparisonCard({
  label, current, previous, pct, dir, tone,
}: {
  label: string;
  current: number;
  previous: number;
  pct: number;
  dir: "up" | "down" | "flat";
  tone: 1 | 2 | 3 | 4 | 5;
}) {
  const Icon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : BarChart3;
  const color = dir === "up" ? "var(--tone-3)" : dir === "down" ? "var(--destructive)" : "var(--muted-foreground)";
  return (
    <div className="border border-border p-5" style={{ background: `var(--tone-${tone}-soft)` }}>
      <div className="eyebrow">{label}</div>
      <div className="mt-2 flex items-baseline gap-3 flex-wrap">
        <div className="font-serif text-3xl">{fmtCurrency(current)}</div>
        <div className="flex items-center gap-1 text-sm font-medium" style={{ color }}>
          <Icon className="size-4" />
          {dir === "flat" ? "≈" : (pct >= 0 ? "+" : "")}{pct.toFixed(1).replace(".", ",")} %
        </div>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        Prej: {fmtCurrency(previous)}
      </div>
    </div>
  );
}

function TopProducts({ vendorItems }: { vendorItems: VItem[] }) {
  const paid = vendorItems.filter((i) => PAID_STATUSES.includes(i.order_status));
  const byTitle = new Map<string, { units: number; revenue: number }>();
  for (const i of paid) {
    const t = i.title || "—";
    const cur = byTitle.get(t) ?? { units: 0, revenue: 0 };
    cur.units += i.quantity;
    cur.revenue += i.line_total;
    byTitle.set(t, cur);
  }
  const list = Array.from(byTitle.entries())
    .map(([title, v]) => ({ title, ...v }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);
  const maxRev = Math.max(1, ...list.map((l) => l.revenue));

  return (
    <div className="border border-border p-5">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="size-4" style={{ color: "var(--tone-4)" }} />
        <div className="eyebrow">Top izdelki</div>
      </div>
      {list.length === 0 ? (
        <div className="text-sm text-muted-foreground">Še ni prodaje.</div>
      ) : (
        <ul className="space-y-3">
          {list.map((p, idx) => (
            <li key={p.title}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate">
                  <span className="font-mono text-xs text-muted-foreground mr-2">{idx + 1}.</span>
                  {p.title}
                </span>
                <span className="font-serif text-base shrink-0" style={{ color: "var(--tone-1)" }}>
                  {fmtCurrency(p.revenue)}
                </span>
              </div>
              <div className="mt-1 h-1.5 bg-muted overflow-hidden">
                <div
                  className="h-full"
                  style={{
                    width: `${(p.revenue / maxRev) * 100}%`,
                    background: `var(--tone-${((idx % 5) + 1)})`,
                  }}
                />
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{fmtInt(p.units)} kos</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusBreakdown({ vendorItems }: { vendorItems: VItem[] }) {
  const orderStatuses = new Map<string, string>();
  for (const i of vendorItems) orderStatuses.set(i.order_id, i.order_status);
  const counts: Record<string, number> = {
    quote_requested: 0, quote_sent: 0, paid: 0, shipped: 0, delivered: 0,
  };
  for (const s of orderStatuses.values()) {
    if (s in counts) counts[s]++;
  }
  const data = [
    { key: "quote_requested", label: "Povpraš.", value: counts.quote_requested, tone: 5 },
    { key: "quote_sent", label: "Predračun", value: counts.quote_sent, tone: 2 },
    { key: "paid", label: "Plačano", value: counts.paid, tone: 4 },
    { key: "shipped", label: "Odposlano", value: counts.shipped, tone: 3 },
    { key: "delivered", label: "Dostavljeno", value: counts.delivered, tone: 1 },
  ];
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <div className="border border-border p-5">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="size-4" style={{ color: "var(--tone-2)" }} />
        <div className="eyebrow">Naročila po statusih</div>
      </div>
      {total === 0 ? (
        <div className="text-sm text-muted-foreground">Še ni naročil.</div>
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} axisLine={{ stroke: "var(--border)" }} tickLine={false} />
              <YAxis tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", borderRadius: 4, fontSize: 12 }}
                formatter={(v: number) => [`${v}`, "Naročila"]}
              />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {data.map((d) => (
                  <Cell key={d.key} fill={`var(--tone-${d.tone})`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function ConversionStats({
  vendorItems,
  inquiries,
  sentQuotes,
}: {
  vendorItems: VItem[];
  inquiries: Array<{ id: string; created_at: string }>;
  sentQuotes: Array<{ status: string }>;
}) {
  const paid = vendorItems.filter((i) => PAID_STATUSES.includes(i.order_status));
  const paidOrderIds = new Set(paid.map((i) => i.order_id));
  const paidOrders = paidOrderIds.size;
  const paidRevenue = paid.reduce((s, i) => s + i.line_total, 0);
  const aov = paidOrders > 0 ? paidRevenue / paidOrders : 0;

  // Quote conversion: accepted / sent
  const sentCount = sentQuotes.filter((q) => ["sent", "accepted", "rejected"].includes(q.status)).length;
  const acceptedCount = sentQuotes.filter((q) => q.status === "accepted").length;
  const quoteConv = sentCount > 0 ? (acceptedCount / sentCount) * 100 : 0;

  // Inquiry → paid order conversion (rough): paid orders / (inquiries + paid orders)
  const totalLeads = inquiries.length + new Set(vendorItems.map((i) => i.order_id)).size;
  const leadConv = totalLeads > 0 ? (paidOrders / totalLeads) * 100 : 0;

  return (
    <div className="border border-border p-5" style={{ background: "var(--tone-3-soft)" }}>
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="size-4" style={{ color: "var(--tone-3)" }} />
        <div className="eyebrow">Učinkovitost</div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Metric label="Povp. naročilo" value={fmtCurrency(aov)} hint={`${fmtInt(paidOrders)} naročil`} />
        <Metric label="Sprejeti predračuni" value={`${quoteConv.toFixed(0).replace(".", ",")} %`} hint={`${fmtInt(acceptedCount)} / ${fmtInt(sentCount)}`} />
        <Metric label="Konverzija povpraš." value={`${leadConv.toFixed(0).replace(".", ",")} %`} hint={`${fmtInt(paidOrders)} plačil`} />
      </div>
    </div>
  );
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground min-h-[2.4em] leading-tight">{label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}


function GeoBreakdown({ vendorItems }: { vendorItems: VItem[] }) {
  const paid = vendorItems.filter((i) => PAID_STATUSES.includes(i.order_status));
  // unique orders
  const seenOrder = new Set<string>();
  const byCity = new Map<string, { orders: number; revenue: number }>();
  for (const i of paid) {
    if (seenOrder.has(i.order_id)) continue;
    seenOrder.add(i.order_id);
    const rawCity = (i.shipping_address?.city || "").trim();
    const city = rawCity.toLowerCase() === "nan" || !rawCity ? "Neznano mesto" : rawCity;
    const cur = byCity.get(city) ?? { orders: 0, revenue: 0 };
    cur.orders++;
    // sum all items of this order
    const orderRev = paid.filter((x) => x.order_id === i.order_id).reduce((s, x) => s + x.line_total, 0);
    cur.revenue += orderRev;
    byCity.set(city, cur);
  }
  const list = Array.from(byCity.entries())
    .map(([city, v]) => ({ city, ...v }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 6);
  const maxRev = Math.max(1, ...list.map((l) => l.revenue));

  return (
    <div className="border border-border p-5">
      <div className="flex items-center gap-2 mb-4">
        <MapPin className="size-4" style={{ color: "var(--tone-5)" }} />
        <div className="eyebrow">Geografski pregled</div>
      </div>
      {list.length === 0 ? (
        <div className="text-sm text-muted-foreground">Še ni podatkov o naslovih.</div>
      ) : (
        <ul className="space-y-2.5">
          {list.map((c, idx) => (
            <li key={c.city}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate">{c.city}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {c.orders} {c.orders === 1 ? "naročilo" : "naročil"} · <span className="font-serif text-sm" style={{ color: "var(--tone-1)" }}>€{c.revenue.toFixed(2)}</span>
                </span>
              </div>
              <div className="mt-1 h-1.5 bg-muted overflow-hidden">
                <div className="h-full" style={{ width: `${(c.revenue / maxRev) * 100}%`, background: `var(--tone-${((idx % 5) + 1)})` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ExportSalesCard({ vendorItems, storeName }: { vendorItems: VItem[]; storeName: string }) {
  const onExport = () => {
    const rows = vendorItems.slice().sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    const header = ["datum", "naročilo", "status", "izdelek", "kolicina", "cena_enote", "skupaj", "mesto", "drzava"];
    const escape = (v: unknown) => {
      const s = v == null ? "" : String(v);
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(";")];
    for (const r of rows) {
      const unit = r.quantity > 0 ? r.line_total / r.quantity : r.line_total;
      lines.push([
        r.created_at ? new Date(r.created_at).toISOString().slice(0, 10) : "",
        r.order_id.slice(0, 8),
        r.order_status,
        r.title ?? "",
        r.quantity,
        unit.toFixed(2),
        r.line_total.toFixed(2),
        r.shipping_address?.city ?? "",
        r.shipping_address?.country ?? "",
      ].map(escape).join(";"));
    }
    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `prodaja_${storeName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}_${date}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <button
      type="button"
      onClick={onExport}
      className="border border-border p-5 hover:border-accent transition-colors h-full block text-left w-full"
    >
      <Download className="size-5" style={{ color: "var(--tone-3)" }} />
      <div className="font-serif text-lg mt-3">Izvoz CSV</div>
      <p className="mt-1 text-sm text-muted-foreground">Vse prodaje za računovodstvo.</p>
    </button>
  );
}



function BuyerPregled({
  data,
  unreadMy,
  receivedQuotesCount,
  myOrders,
  wishlistCount,
  onTab,
}: {
  data: any;
  unreadMy: number;
  receivedQuotesCount: number;
  myOrders: Array<{ id: string; created_at: string; status: string; amount_total: number }>;
  wishlistCount: number;
  onTab: (t: TabId) => void;
}) {
  const activeOrders = myOrders.filter((o) => !["delivered", "canceled", "refunded", "failed"].includes(o.status)).length;
  const totalSpent = myOrders.filter((o) => ["paid", "shipped", "delivered"].includes(o.status)).reduce((s, o) => s + Number(o.amount_total ?? 0), 0);
  const lastOrder = myOrders[0];

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow">Pregled kupca</div>
        <h2 className="font-serif text-2xl mt-1">Moj nakupovalni račun</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <button type="button" onClick={() => onTab("narocila")} className="text-left">
          <StatCard label="Naročila" value={myOrders.length} icon={ShoppingBag} hint={activeOrders ? `${activeOrders} aktivnih` : `Porabljeno €${totalSpent.toFixed(2)}`} tone={2} />
        </button>
        <button type="button" onClick={() => onTab("predracuni-prejeti")} className="text-left">
          <StatCard label="Predračuni" value={receivedQuotesCount} icon={FileText} hint="Prejete ponudbe" tone={3} />
        </button>
        <button type="button" onClick={() => onTab("povprasevanja-moja")} className="text-left">
          <StatCard label="Povpraševanja" value={unreadMy} icon={MessageSquare} hint={unreadMy ? "Neprebrana" : "Pregled vseh"} tone={4} />
        </button>
        <button type="button" onClick={() => onTab("zelje")} className="text-left">
          <StatCard label="Seznam želja" value={wishlistCount} icon={Heart} hint={`${wishlistCount} izdelkov`} tone={1} />
        </button>
      </div>

      {lastOrder && (
        <div className="border border-border p-6">
          <div className="eyebrow">Zadnje naročilo</div>
          <div className="mt-2 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-serif text-lg">#{lastOrder.id.slice(0, 8)}</div>
              <div className="text-xs text-muted-foreground">
                {new Date(lastOrder.created_at).toLocaleDateString("sl-SI", { day: "numeric", month: "long", year: "numeric" })} · {lastOrder.status}
              </div>
            </div>
            <div className="font-serif text-2xl text-accent">€{Number(lastOrder.amount_total ?? 0).toFixed(2)}</div>
          </div>
          <button onClick={() => onTab("narocila")} className="mt-4 text-sm text-accent hover:underline">
            Vsa naročila →
          </button>
        </div>
      )}

      {!data.vendor && (
        <div className="border border-border p-6 bg-accent/5">
          <div className="eyebrow">Postanite prodajalec</div>
          <h2 className="font-serif text-2xl mt-2">Odprite svojo trgovino</h2>
          <p className="mt-2 text-sm text-muted-foreground">Predstavite svoje izdelke kupcem po vsej Sloveniji. Prve 3 mesece brezplačno.</p>
          <Link to="/postani-prodajalec" className="mt-4 inline-flex bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90">
            Odpri trgovino →
          </Link>
        </div>
      )}

    </div>
  );
}

function QuickAction({
  icon: Icon,
  title,
  desc,
  onClick,
  to,
}: {
  icon: any;
  title: string;
  desc: string;
  onClick?: () => void;
  to?: "/moji-izdelki";
}) {
  const inner = (
    <>
      <Icon className="size-5 text-accent" />
      <div className="font-serif text-lg mt-3">{title}</div>
      <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
    </>
  );
  const cls = "border border-border p-5 hover:border-accent transition-colors h-full block text-left w-full";
  if (to) return <Link to={to} className={cls}>{inner}</Link>;
  return <button type="button" onClick={onClick} className={cls}>{inner}</button>;
}


/* ---------- Tab: Trgovina ---------- */

function TrgovinaTab({
  vendor,
  sub,
  email,
}: {
  vendor: any;
  sub: any;
  email: string | null;
}) {
  if (!vendor) {
    return (
      <div className="border border-border p-8">
        <div className="eyebrow">Postanite prodajalec</div>
        <h2 className="font-serif text-2xl mt-2">Še nimate trgovine</h2>
        <Link
          to="/postani-prodajalec"
          className="mt-4 inline-flex bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90"
        >
          Odpri trgovino →
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <div className="border border-border p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow">Moja trgovina</div>
            <h2 className="font-serif text-3xl mt-2">{vendor.store_name}</h2>
            <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center gap-1.5 ${
                  vendor.status === "active" ? "text-accent" : ""
                }`}
              >
                <CheckCircle2 className="size-3.5" />
                {vendor.status}
              </span>
              {vendor.city && <span>· {vendor.city}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/trgovina"
              className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              <Pencil className="size-4" /> Uredi
            </Link>
            {vendor.slug && (
              <Link
                to="/prodajalci/$slug"
                params={{ slug: vendor.slug }}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm hover:opacity-90"
              >
                <ExternalLink className="size-4" /> Javni profil
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="border border-border p-6">
        <div className="eyebrow">Naročnina</div>
        {sub ? (
          <>
            <h3 className="font-serif text-2xl mt-2">
              {sub.product_name ?? "Najem trgovine"}
            </h3>
            <div className="mt-3 grid sm:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Status</div>
                <div
                  className={`mt-1 font-medium ${
                    sub.status === "active" ? "text-accent" : "text-destructive"
                  }`}
                >
                  {sub.status}
                </div>
              </div>
              {sub.amount && (
                <div>
                  <div className="text-xs text-muted-foreground">Cena</div>
                  <div className="mt-1 font-medium">
                    {sub.amount} {sub.currency.toUpperCase()} / leto
                  </div>
                </div>
              )}
              {sub.current_period_end && (
                <div>
                  <div className="text-xs text-muted-foreground">Velja do</div>
                  <div className="mt-1 font-medium">
                    {new Date(sub.current_period_end).toLocaleDateString("sl-SI")}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-muted-foreground">
              Za aktivacijo trgovine je potrebna aktivna letna naročnina.
            </p>
            <a
              href={`https://buy.stripe.com/14A4gy12K2WM0ZS8uD6c00q${email ? `?prefilled_email=${encodeURIComponent(email)}` : ""}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90"
            >
              Aktiviraj trgovino →
            </a>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Tab: Izdelki ---------- */

function IzdelkiTab({
  isAdmin,
  vendor,
  productCount,
  isActiveMember,
  email,
}: {
  isAdmin: boolean;
  vendor: any;
  productCount: number;
  isActiveMember: boolean;
  email: string | null;
}) {
  return (
    <div className="border border-border p-8">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="eyebrow">Upravljanje izdelkov</div>
          <h2 className="font-serif text-3xl mt-2">
            {isAdmin ? "Vsi izdelki" : "Moji izdelki"}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-md">
            {isAdmin
              ? "Kot skrbnik lahko urejate ali brišete vse izdelke v nakupovalnem središču."
              : isActiveMember
                ? "Dodajte nove izdelke ali uredite obstoječe v vaši trgovini."
                : "Za dodajanje in urejanje izdelkov je potrebna aktivna naročnina."}
          </p>
        </div>
        {isAdmin || isActiveMember ? (
          <div className="flex flex-wrap gap-3">
            {(isAdmin ? vendor : true) && (
              <Link
                to="/moji-izdelki"
                search={{ new: 1 }}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90"
              >
                <Plus className="size-4" /> Nov izdelek
              </Link>
            )}
            <Link
              to="/moji-izdelki"
              className="border border-border px-5 py-2.5 text-sm hover:bg-muted"
            >
              {isAdmin ? "Uredi vse izdelke" : `Uredi izdelke (${productCount})`}
            </Link>
          </div>
        ) : (
          <a
            href={`https://buy.stripe.com/14A4gy12K2WM0ZS8uD6c00q${email ? `?prefilled_email=${encodeURIComponent(email)}` : ""}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90"
          >
            Aktiviraj naročnino →
          </a>
        )}
      </div>
    </div>
  );
}

/* ---------- Tab: Prodaja ---------- */

function ProdajaTab({ isAdmin }: { isAdmin: boolean }) {
  return (
    <div className="border border-border p-8">
      <div className="eyebrow">Zgodovina prodaj</div>
      <h2 className="font-serif text-3xl mt-2">
        {isAdmin ? "Vsa naročila in statistika" : "Moja prodaja"}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-md">
        Podroben pregled naročil, statusi in podatki za odpremo.
      </p>
      <Link
        to="/prodaja"
        className="mt-4 inline-flex bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90"
      >
        <History className="size-4 mr-2" /> Odpri zgodovino prodaj →
      </Link>
    </div>
  );
}

/* ---------- Tab: Naročila (kupec) ---------- */

const STATUS_LABEL: Record<string, string> = {
  pending: "V obdelavi",
  paid: "Plačano",
  shipped: "Odposlano",
  delivered: "Dostavljeno",
  cancelled: "Preklicano",
  refunded: "Povrnjeno",
};

function NarocilaList() {
  const fetchOrders = useServerFn(getMyOrders);
  const { data, isLoading } = useQuery({ queryKey: ["my-orders"], queryFn: () => fetchOrders() });
  if (isLoading) return <div className="text-muted-foreground">Nalagam…</div>;
  if (!data || data.orders.length === 0)
    return (
      <div className="border border-dashed border-border p-12 text-center">
        <p className="text-muted-foreground">Še nimate naročil.</p>
        <Link to="/izdelki" className="mt-4 inline-block underline underline-offset-4">
          Razišči izdelke →
        </Link>
      </div>
    );
  return (
    <div className="space-y-6">
      {data.orders.map((o) => (
        <div key={o.id} className="border border-border">
          <div className="flex items-center justify-between flex-wrap gap-3 p-5 bg-muted/30 border-b border-border">
            <div>
              <div className="text-xs text-muted-foreground">Naročilo #{o.id.slice(0, 8)}</div>
              <div className="text-sm">
                {new Date(o.created_at).toLocaleDateString("sl-SI", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs uppercase tracking-wider px-2 py-1 bg-background border border-border">
                {STATUS_LABEL[o.status] ?? o.status}
              </span>
              <div className="font-serif text-lg text-accent">€{o.amount_total.toFixed(2)}</div>
            </div>
          </div>
          <div className="divide-y divide-border">
            {o.items.map((it) => (
              <div
                key={it.id}
                className="p-4 flex items-center justify-between gap-4 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{it.title}</div>
                  {it.vendor_name && (
                    <div className="text-xs text-muted-foreground">{it.vendor_name}</div>
                  )}
                </div>
                <div className="text-muted-foreground">
                  {it.quantity}× <span className="text-accent">€{it.unit_price.toFixed(2)}</span>
                </div>
                <div className="font-serif text-accent">€{it.line_total.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Tab: Želje ---------- */

function ZeljeList() {
  const fetchWishlist = useServerFn(getMyWishlist);
  const { data, isLoading } = useQuery({
    queryKey: ["my-wishlist"],
    queryFn: () => fetchWishlist(),
  });
  if (isLoading) return <div className="text-muted-foreground">Nalagam…</div>;
  if (!data || data.products.length === 0)
    return (
      <div className="border border-dashed border-border p-12 text-center">
        <p className="text-muted-foreground">Vaš seznam želja je prazen.</p>
        <Link to="/izdelki" className="mt-4 inline-block underline underline-offset-4">
          Razišči izdelke →
        </Link>
      </div>
    );
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {data.products.map((p) => (
        <Link
          key={p.id}
          to="/izdelki/$slug"
          params={{ slug: p.slug }}
          className="border border-border hover:border-accent transition-colors"
        >
          {p.featured_image_url && (
            <div className="aspect-square overflow-hidden bg-muted">
              <img
                src={p.featured_image_url}
                alt={p.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          )}
          <div className="p-3">
            <div className="text-sm font-medium truncate">{p.title}</div>
            {p.vendor?.store_name && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {p.vendor.store_name}
              </div>
            )}
            {p.price != null && (
              <div className="mt-1 text-sm text-accent font-serif">
                €{Number(p.price).toFixed(2)}
              </div>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
}

/* ---------- Quotes: status helpers ---------- */

const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: "Osnutek",
  sent: "Poslan",
  accepted: "Sprejet",
  rejected: "Zavrnjen",
  expired: "Potekel",
};

function QuoteStatusBadge({ status }: { status: QuoteStatus }) {
  const tone =
    status === "accepted"
      ? "bg-accent/10 text-accent border-accent/40"
      : status === "rejected" || status === "expired"
        ? "bg-destructive/10 text-destructive border-destructive/40"
        : status === "sent"
          ? "bg-primary/10 text-primary border-primary/40"
          : "bg-muted text-muted-foreground border-border";
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${tone}`}>
      {QUOTE_STATUS_LABEL[status]}
    </span>
  );
}

/* ---------- Tab: Prejeti predračuni ---------- */

function ReceivedQuotes({ rows, userId }: { rows: Quote[]; userId?: string }) {
  const qc = useQueryClient();
  const respondFn = useServerFn(respondToQuote);
  const mut = useMutation({
    mutationFn: (vars: { id: string; action: "accept" | "reject" }) =>
      respondFn({ data: vars }),
    onSuccess: () => {
      toast.success("Odgovor poslan");
      qc.invalidateQueries({ queryKey: ["received-quotes", userId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (rows.length === 0)
    return (
      <div className="border border-dashed border-border p-12 text-center">
        <p className="text-muted-foreground">Še nimate prejetih predračunov.</p>
      </div>
    );

  return (
    <div className="space-y-4">
      {rows.map((q) => (
        <div key={q.id} className="border border-border p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <QuoteStatusBadge status={q.status} />
                <div className="text-xs text-muted-foreground">
                  {new Date(q.created_at).toLocaleDateString("sl-SI")}
                </div>
              </div>
              <h3 className="font-serif text-xl mt-2">{q.title}</h3>
              <div className="text-sm text-muted-foreground mt-1">
                Od: {q.vendor?.store_name ?? "Prodajalec"}
              </div>
              {q.valid_until && (
                <div className="text-xs text-muted-foreground mt-1">
                  Velja do {new Date(q.valid_until).toLocaleDateString("sl-SI")}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="font-serif text-2xl text-accent">
                €{Number(q.total).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/predracun/$id"
              params={{ id: q.id }}
              className="border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              Odpri →
            </Link>
            {q.status === "sent" && (
              <>
                <button
                  disabled={mut.isPending}
                  onClick={() => mut.mutate({ id: q.id, action: "accept" })}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50"
                >
                  Sprejmi
                </button>
                <button
                  disabled={mut.isPending}
                  onClick={() => mut.mutate({ id: q.id, action: "reject" })}
                  className="border border-destructive/40 text-destructive px-4 py-2 text-sm hover:bg-destructive/5 disabled:opacity-50"
                >
                  Zavrni
                </button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Tab: Poslani predračuni ---------- */

function SentQuotes({ rows, userId }: { rows: Quote[]; userId?: string }) {
  const qc = useQueryClient();
  const sendFn = useServerFn(sendQuote);
  const delFn = useServerFn(deleteQuote);
  const paidFn = useServerFn(markQuotePaid);
  const sendMut = useMutation({
    mutationFn: (id: string) => sendFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Predračun poslan");
      qc.invalidateQueries({ queryKey: ["sent-quotes", userId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Predračun izbrisan");
      qc.invalidateQueries({ queryKey: ["sent-quotes", userId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const paidMut = useMutation({
    mutationFn: (id: string) => paidFn({ data: { id } }),
    onSuccess: (res) => {
      toast.success(
        res?.invoice_number
          ? `Označeno kot plačano · Račun ${res.invoice_number} poslan stranki.`
          : "Označeno kot plačano. Račun je ustvarjen.",
      );
      qc.invalidateQueries({ queryKey: ["sent-quotes", userId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {rows.length} {rows.length === 1 ? "predračun" : "predračunov"}
        </div>
        <Link
          to="/predracun/nov"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm hover:opacity-90"
        >
          <Plus className="size-4" /> Nov predračun
        </Link>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <p className="text-muted-foreground">Še niste izdali predračunov.</p>
        </div>
      ) : (
        rows.map((q) => (
          <div key={q.id} className="border border-border p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <QuoteStatusBadge status={q.status} />
                  <div className="text-xs text-muted-foreground">
                    {new Date(q.created_at).toLocaleDateString("sl-SI")}
                  </div>
                </div>
                <h3 className="font-serif text-xl mt-2">{q.title}</h3>
                <div className="text-sm text-muted-foreground mt-1">
                  Za: {q.buyer_name ?? q.buyer_email}
                </div>
              </div>
              <div className="text-right">
                <div className="font-serif text-2xl text-accent">
                  €{Number(q.total).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/predracun/$id"
                params={{ id: q.id }}
                className="border border-border px-4 py-2 text-sm hover:bg-muted"
              >
                Odpri →
              </Link>
              {q.status === "draft" && (
                <>
                  <Link
                    to="/predracun/uredi/$id"
                    params={{ id: q.id }}
                    className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm hover:bg-muted"
                  >
                    <Pencil className="size-3.5" /> Uredi
                  </Link>
                  <button
                    disabled={sendMut.isPending}
                    onClick={() => sendMut.mutate(q.id)}
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50"
                  >
                    <Send className="size-3.5" /> Pošlji
                  </button>
                  <button
                    disabled={delMut.isPending}
                    onClick={() => {
                      if (confirm("Izbrišem ta predračun?")) delMut.mutate(q.id);
                    }}
                    className="inline-flex items-center gap-2 border border-destructive/40 text-destructive px-4 py-2 text-sm hover:bg-destructive/5 disabled:opacity-50"
                  >
                    <Trash2 className="size-3.5" /> Izbriši
                  </button>
                </>
              )}
              {(q.status === "sent" || q.status === "accepted") && !q.paid_at && (
                <button
                  disabled={paidMut.isPending}
                  onClick={() => {
                    if (confirm("Označim ta predračun kot plačan? Ustvaril se bo račun in poslal kupcu na e-pošto.")) {
                      paidMut.mutate(q.id);
                    }
                  }}
                  className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50"
                >
                  <CheckCircle2 className="size-3.5" /> Označi kot plačan
                </button>
              )}
              {q.paid_at && (
                <span className="inline-flex items-center gap-2 text-sm text-accent">
                  <CheckCircle2 className="size-3.5" /> Plačan {new Date(q.paid_at).toLocaleDateString("sl-SI")}
                </span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ---------- Tab: Admin ---------- */

function AdminTab() {
  return (
    <div className="border border-accent/40 bg-accent/5 p-8">
      <div className="flex items-start gap-4">
        <Shield className="size-6 text-accent shrink-0 mt-1" />
        <div className="flex-1">
          <div className="eyebrow text-accent">Skrbnik</div>
          <h2 className="font-serif text-2xl mt-2">Skrbniške pravice</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Imate skrbniške pravice za upravljanje vsebine nakupovalnega središča.
          </p>
          <Link
            to="/admin"
            className="mt-4 inline-flex bg-foreground text-background px-5 py-2.5 text-sm hover:opacity-90"
          >
            Odpri admin →
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ---------- Inquiries block ---------- */

type InqRow = {
  id: string;
  customer_email: string;
  customer_name: string | null;
  message: string;
  read_at: string | null;
  created_at: string;
  product?: { id: string; title: string; slug: string } | null;
  vendor?: { id: string; store_name: string; slug: string | null } | null;
};

function InquiriesBlock({
  title,
  emptyText,
  rows,
  showCustomer,
  showVendor,
}: {
  title: string;
  emptyText: string;
  rows: InqRow[];
  showCustomer?: boolean;
  showVendor?: boolean;
}) {
  return (
    <div className="border border-border p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">Povpraševanja</div>
          <h2 className="font-serif text-2xl mt-2">{title}</h2>
        </div>
        <div className="text-xs text-muted-foreground">
          {rows.length} {rows.length === 1 ? "sporočilo" : "sporočil"}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="mt-6 divide-y divide-border">
          {rows.map((r) => (
            <li key={r.id} className="py-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-sm flex items-center gap-2">
                    {!r.read_at && (
                      <span className="size-2 rounded-full bg-accent shrink-0" />
                    )}
                    {r.product?.slug ? (
                      <Link
                        to="/izdelki/$slug"
                        params={{ slug: r.product.slug }}
                        className="underline underline-offset-4 hover:text-accent"
                      >
                        {r.product.title}
                      </Link>
                    ) : (
                      <span>{r.product?.title ?? "Izdelek"}</span>
                    )}
                    {showVendor && r.vendor && (
                      <span className="text-muted-foreground">
                        · {r.vendor.store_name}
                      </span>
                    )}
                  </div>
                  {showCustomer && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Od: {r.customer_name ?? r.customer_email} · {r.customer_email}
                    </div>
                  )}
                  <p className="mt-2 text-sm whitespace-pre-wrap text-muted-foreground">
                    {r.message}
                  </p>
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">
                  {new Date(r.created_at).toLocaleDateString("sl-SI")}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VisitsChart() {
  const [range, setRange] = useState<VisitRange>("1m");
  const fetchStats = useServerFn(getMyVendorVisitStats);
  const { data, isLoading } = useQuery({
    queryKey: ["vendor-visits", range],
    queryFn: () => fetchStats({ data: { range } }),
    staleTime: 60_000,
  });

  const ranges: { id: VisitRange; label: string }[] = [
    { id: "1m", label: "1 mesec" },
    { id: "1y", label: "1 leto" },
    { id: "5y", label: "5 let" },
    { id: "all", label: "Od začetka" },
  ];

  const formatXAxis = (key: string) => {
    if (!data) return key;
    if (data.granularity === "day") {
      const d = new Date(key);
      return d.toLocaleDateString("sl-SI", { day: "2-digit", month: "2-digit" });
    }
    const [y, m] = key.split("-");
    return `${m}/${y.slice(2)}`;
  };

  return (
    <div className="border border-border p-5 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Obiski vaše trgovine</div>
          <h3 className="font-serif text-xl mt-1">Kolikokrat so obiskovalci obiskali vašo trgovino</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Kolikokrat so obiskovalci obiskali vašo trgovino in si ogledali vaše izdelke.
          </p>
        </div>
        <div className="flex gap-1 flex-wrap">
          {ranges.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRange(r.id)}
              className={`px-3 py-1.5 text-xs border ${range === r.id ? "bg-foreground text-background border-foreground" : "border-border hover:border-accent"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground py-10 text-center">Nalagam…</div>
      ) : !data || data.total === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border">
          V izbranem obdobju še ni obiskov.
        </div>
      ) : (
        <>
          <div className="border border-border p-4">
            <div className="text-xs text-muted-foreground">Skupaj obiskov</div>
            <div className="font-serif text-3xl mt-1">{data.total}</div>
          </div>

          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="visitsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tickFormatter={formatXAxis} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  labelFormatter={formatXAxis}
                  formatter={(v: number) => [v, "Obiski"]}
                  contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))" }}
                />
                <Area type="monotone" dataKey="count" stroke="hsl(var(--accent))" fill="url(#visitsFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
